/**
 * Trade Template Service
 * Handles storage and retrieval of trade templates
 * Uses localStorage for UI phase (will be replaced with API in Phase 3)
 */

import { TradeTemplate, TemplateFormData } from "../types/tradeTemplate";

const STORAGE_KEY_PREFIX = "tradeLog_templates_";

/**
 * Get storage key for user's templates
 */
const getStorageKey = (userId: string): string => {
  return `${STORAGE_KEY_PREFIX}${userId}`;
};

/**
 * Get all templates for a user
 */
export const getTemplates = (userId: string): TradeTemplate[] => {
  try {
    const storageKey = getStorageKey(userId);
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      return [];
    }
    const templates = JSON.parse(stored);
    // Convert date strings back to Date objects
    return templates.map((t: TradeTemplate) => ({
      ...t,
      createdAt: new Date(t.createdAt),
      updatedAt: new Date(t.updatedAt),
      lastUsedAt: t.lastUsedAt ? new Date(t.lastUsedAt) : undefined,
    }));
  } catch (error) {
    console.error("Error loading templates:", error);
    return [];
  }
};

/**
 * Get a single template by ID
 */
export const getTemplate = (userId: string, templateId: string): TradeTemplate | null => {
  const templates = getTemplates(userId);
  return templates.find((t) => t.id === templateId) || null;
};

/**
 * Save a new template
 */
export const saveTemplate = (userId: string, templateData: TemplateFormData): TradeTemplate => {
  const templates = getTemplates(userId);
  
  const newTemplate: TradeTemplate = {
    id: `template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name: templateData.name,
    description: templateData.description,
    formData: templateData.formData,
    userId,
    createdAt: new Date(),
    updatedAt: new Date(),
    useCount: 0,
  };

  templates.push(newTemplate);
  saveTemplates(userId, templates);
  
  return newTemplate;
};

/**
 * Update an existing template
 */
export const updateTemplate = (
  userId: string,
  templateId: string,
  templateData: Partial<TemplateFormData>
): TradeTemplate | null => {
  const templates = getTemplates(userId);
  const index = templates.findIndex((t) => t.id === templateId);
  
  if (index === -1) {
    return null;
  }

  const updatedTemplate: TradeTemplate = {
    ...templates[index],
    ...(templateData.name && { name: templateData.name }),
    ...(templateData.description !== undefined && { description: templateData.description }),
    ...(templateData.formData && { formData: templateData.formData }),
    updatedAt: new Date(),
  };

  templates[index] = updatedTemplate;
  saveTemplates(userId, templates);
  
  return updatedTemplate;
};

/**
 * Delete a template
 */
export const deleteTemplate = (userId: string, templateId: string): boolean => {
  const templates = getTemplates(userId);
  const filtered = templates.filter((t) => t.id !== templateId);
  
  if (filtered.length === templates.length) {
    return false; // Template not found
  }

  saveTemplates(userId, filtered);
  return true;
};

/**
 * Increment template use count
 */
export const incrementTemplateUse = (userId: string, templateId: string): void => {
  const templates = getTemplates(userId);
  const template = templates.find((t) => t.id === templateId);
  
  if (template) {
    template.useCount = (template.useCount || 0) + 1;
    template.lastUsedAt = new Date();
    saveTemplates(userId, templates);
  }
};

/**
 * Save templates to localStorage
 */
const saveTemplates = (userId: string, templates: TradeTemplate[]): void => {
  try {
    const storageKey = getStorageKey(userId);
    localStorage.setItem(storageKey, JSON.stringify(templates));
  } catch (error) {
    console.error("Error saving templates:", error);
    throw new Error("Failed to save template");
  }
};

